import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  ArrowRight,
  Network,
  GitBranch,
  Star,
  Grid3X3,
  MessageSquare,
  Layers,
  PencilRuler,
} from "lucide-react";
import { TOPOLOGY_TEMPLATES, type TopologyTemplate } from "../../data/topology-templates.js";
import { useCreateTopology } from "../../api/hooks.js";
import { useWorldStore } from "../../stores/world.js";
import { useCanvasStore } from "../../stores/canvas.js";
import { useNavigate } from "react-router-dom";

const TYPE_ICONS: Record<string, any> = {
  pipeline: ArrowRight,
  hierarchical: GitBranch,
  star: Star,
  mesh: Grid3X3,
  custom: Network,
};

interface TemplateGalleryProps {
  open: boolean;
  onClose: () => void;
}

export function TemplateGallery({ open, onClose }: TemplateGalleryProps) {
  const createTopology = useCreateTopology();
  const navigate = useNavigate();
  const setMode = useWorldStore((s) => s.setMode);
  const setViewMode = useWorldStore((s) => s.setViewMode);

  async function handleUseTemplate(template: TopologyTemplate) {
    try {
      const result = await createTopology.mutateAsync({
        definition: template.definition,
      });
      onClose();
      navigate(`/topology/${result.id}`);
    } catch {
      // Error handled by React Query
    }
  }

  /** Start from scratch in the design-mode builder. */
  function handleBlankCanvas() {
    // When a topology is selected, navigating away clears it and the design
    // canvas blanks itself; resetting here would re-seed during the transition.
    // Only force a reset when we're already on a blank build.
    if (!useWorldStore.getState().selectedTopologyId) {
      useCanvasStore.getState().reset();
    }
    setViewMode("flow");
    setMode("design");
    onClose();
    navigate("/topology");
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
          className="fixed right-0 top-0 bottom-0 w-[440px] bg-surface-light border-l border-border-subtle/20 z-40 flex flex-col shadow-2xl shadow-black/40"
        >
          {/* Header */}
          <div className="px-5 py-4 border-b border-border-subtle/15 flex items-center justify-between shrink-0">
            <div>
              <h2 className="text-sm font-medium text-text-primary">Topology Templates</h2>
              <p className="text-[11px] text-text-muted mt-0.5">
                Pre-built patterns to get started quickly
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-text-muted hover:text-text-primary transition-[color,transform] duration-200 active:scale-[0.96] p-1"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Template grid */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {/* Blank canvas — build your own in design mode */}
            <button
              onClick={handleBlankCanvas}
              className="group flex w-full items-center gap-3 rounded-xl border border-dashed border-border-accent/40 bg-surface/20 p-4 text-left transition-[border-color,background-color,transform] duration-200 hover:border-brand-cyan/40 hover:bg-surface/40 active:scale-[0.99]"
            >
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-cyan/10">
                <PencilRuler className="h-[18px] w-[18px] text-brand-cyan" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-medium text-text-primary">Blank canvas</h3>
                <p className="mt-0.5 text-xs text-text-muted">
                  Build your own — drag agents, connect them, and save.
                </p>
              </div>
              <ArrowRight className="h-4 w-4 shrink-0 text-text-muted transition-colors group-hover:text-brand-cyan" />
            </button>

            <div className="px-1 pt-1 text-[10px] font-medium uppercase tracking-wide text-text-muted">
              Templates
            </div>

            {TOPOLOGY_TEMPLATES.map((template) => {
              const Icon = TYPE_ICONS[template.type] ?? Network;
              return (
                <div
                  key={template.id}
                  className="group p-4 rounded-xl bg-surface/30 border border-border-subtle/10 hover:border-brand-purple/20 transition-colors duration-200"
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-9 h-9 rounded-lg bg-brand-purple/10 flex items-center justify-center shrink-0">
                      <Icon className="w-4.5 h-4.5 text-brand-purple" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-text-primary">
                        {template.name}
                      </h3>
                      <p className="text-xs text-text-muted mt-0.5 leading-relaxed">
                        {template.description}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 text-[11px] text-text-muted">
                      <span className="tabular-nums">{template.agentCount} agents</span>
                      <span className="tabular-nums">{template.definition.edges.length} connections</span>
                      <span className="capitalize">{template.type}</span>
                    </div>
                    <button
                      onClick={() => handleUseTemplate(template)}
                      disabled={createTopology.isPending}
                      className="flex items-center gap-1.5 bg-brand-purple/10 hover:bg-brand-purple/20 text-brand-purple px-3 py-1 rounded-lg text-xs font-medium transition-[color,background-color,transform] duration-200 active:scale-[0.96] disabled:opacity-40"
                    >
                      Use
                      <ArrowRight className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
